// src/components/common/ErrorBoundary.jsx
//
// A rede de segurança que o app não tinha.
//
// Um erro lançado durante o render (um hook condicional, um `undefined.map`)
// desmonta a árvore React INTEIRA quando não há boundary acima dele. O `#root`
// fica vazio e sobra o fundo do documento — no tema escuro, uma tela preta sem
// nenhuma pista, e a única saída é matar o app. Foi exatamente o sintoma do
// logout e do fim do objetivo em setembro/2026 (ver `aoAtualizarAba` em
// PlataformaPessoal.jsx).
//
// Este componente não conserta erro nenhum: ele transforma "tela preta muda"
// em "algo deu errado + recarregar", e loga o erro no console para o relato
// de bug ter o que mostrar. É class component porque `componentDidCatch` /
// `getDerivedStateFromError` ainda não têm equivalente em hooks.

import React from 'react';
import { reportarErro } from '../../telemetria/errosDoApp';
import s from './ErrorBoundary.module.scss';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { erro: null };
  }

  static getDerivedStateFromError(erro) {
    return { erro };
  }

  componentDidCatch(erro, info) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] erro de render:', erro, info?.componentStack);
    // E para o painel (App › Crashes): é o único lugar que sabe QUAL componente
    // lançou — a primeira linha do componentStack.
    reportarErro({
      tipo: 'react',
      mensagem: erro?.message || String(erro),
      stack: erro?.stack,
      origem: String(info?.componentStack || '').trim().split('\n')[0] || null,
    });
  }

  recarregar = () => {
    // Reload inteiro, e não `setState({ erro: null })`: o estado que causou o
    // erro continuaria lá e a tela voltaria a quebrar no mesmo lugar.
    window.location.reload();
  };

  render() {
    if (!this.state.erro) return this.props.children;
    return (
      <div className={s.tela} role="alert">
        <p className={s.titulo}>Algo deu errado por aqui</p>
        <p className={s.apoio}>
          Não foi você. Toque para recarregar — se continuar, feche e abra o app.
        </p>
        <button type="button" className={s.botao} onClick={this.recarregar}>
          Recarregar
        </button>
      </div>
    );
  }
}
